import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { PencilIcon, PlusIcon, UsersIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

function Organizations() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const orgsRef = collection(db, "organizations");
        const q = query(orgsRef, orderBy("name"));
        const querySnapshot = await getDocs(q);

        const orgsList = [];
        querySnapshot.forEach((doc) => {
          orgsList.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setOrganizations(orgsList);
        setFilteredOrganizations(orgsList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching organizations:", error);
        toast.error("Error loading organizations");
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  const handleEditOrganization = (orgId) => {
    navigate(`/admin/organizations/${orgId}`);
  };

  const handleViewUsers = (orgId) => {
    navigate(`/admin/users?org=${orgId}`);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (value.trim() === "") {
      setFilteredOrganizations(organizations);
    } else {
      const filtered = organizations.filter((org) =>
        org.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOrganizations(filtered);
    }
  };

  const handleResetSearch = () => {
    setSearchTerm("");
    setFilteredOrganizations(organizations);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading organizations...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-primary-600">
                  Organizations
                </h1>
              </div>
              <button
                onClick={() => navigate("/admin/organizations/new")}
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center space-x-2"
              >
                <PlusIcon className="w-4 h-4" />
                <span>New</span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Search organizations by name..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              {searchTerm && (
                <button
                  onClick={handleResetSearch}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Organizations Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrganizations.map((org) => (
                  <tr
                    key={org.id}
                    onClick={() => navigate(`/admin/organizations/${org.id}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {org.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {org.contact_email || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          org.active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {org.active ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {org.date_created
                        ? org.date_created.toDate
                          ? org.date_created.toDate().toLocaleDateString()
                          : new Date(org.date_created).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewUsers(org.id);
                          }}
                          className="text-blue-600 hover:text-blue-900 transition-colors p-1"
                          title="View Users"
                        >
                          <UsersIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditOrganization(org.id);
                          }}
                          className="text-primary-600 hover:text-primary-900 transition-colors p-1"
                          title="Edit Organization"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredOrganizations.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No organizations found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Organizations;
